import { useState } from 'react'
import type { ResponseMap } from '../../types'
import { LikertGroup } from './LikertGroup'
import styles from './DimensionBlock.module.css'

interface Item {
  id: string
  text: string
  reverseCoded?: boolean
  noteText?: string
}

interface Props {
  dimKey: string
  label: string
  subtitle: string
  icon: string
  items: Item[]
  responses: ResponseMap
  onResponse: (itemId: string, value: number) => void
  variant?: 'aqua' | 'magenta'
  // Optional live score pills (calculated by parent)
  readinessScore?: number | null
  resistanceScore?: number | null
  scoreLabel?: string | null  // for HFA single score
}

export function DimensionBlock({
  dimKey,
  label,
  subtitle,
  icon,
  items,
  responses,
  onResponse,
  variant = 'aqua',
  readinessScore,
  resistanceScore,
  scoreLabel,
}: Props) {
  const [collapsed, setCollapsed] = useState(false)

  return (
    <div className={`${styles.block} ${collapsed ? styles.closed : ''}`}>
      <div className={styles.head} onClick={() => setCollapsed(c => !c)}>
        <span className={styles.icon}>{icon}</span>
        <div className={styles.titleWrap}>
          <div className={styles.title}>{label}</div>
          <div className={styles.sub}>{subtitle}</div>
        </div>
        <div className={styles.pills}>
          {readinessScore !== undefined && (
            <div className={styles.pillWrap}>
              <div className={`${styles.pill} ${readinessScore !== null ? styles.pillActive : ''}`}>
                {readinessScore !== null ? `${readinessScore}%` : '—'}
              </div>
              <div className={styles.pillLabel}>Readiness</div>
            </div>
          )}
          {resistanceScore !== undefined && (
            <div className={styles.pillWrap}>
              <div className={`${styles.pill} ${resistanceScore !== null ? styles.pillRes : ''}`}>
                {resistanceScore !== null ? `${resistanceScore}%` : '—'}
              </div>
              <div className={styles.pillLabelRes}>Resistance</div>
            </div>
          )}
          {scoreLabel !== undefined && (
            <div className={styles.pillWrap}>
              <div className={`${styles.pill} ${scoreLabel ? styles.pillActive : ''}`}>
                {scoreLabel ?? '—'}
              </div>
              <div className={styles.pillLabel}>Score</div>
            </div>
          )}
        </div>
        <span className={`${styles.chevron} ${collapsed ? styles.chevronClosed : ''}`}>▼</span>
      </div>

      {!collapsed && (
        <div className={styles.body} key={dimKey}>
          {items.map(item => (
            <div key={item.id} className={styles.itemRow}>
              <span className={styles.itemId}>{item.id}</span>
              <div className={styles.itemQ}>
                {item.text}
                {item.noteText && (
                  <div className={styles.itemNote}>{item.noteText}</div>
                )}
              </div>
              <LikertGroup
                itemId={item.id}
                value={responses[item.id] ?? null}
                onChange={val => onResponse(item.id, val)}
                variant={variant}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
